import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import ReactHotkeys from 'react-hot-keys';
import styled, { css } from 'styled-components';

import { CrossSvg } from '../assets/icons';
import theme from '../assets/theme';
import { zIndexes } from '../utils/zIndexes';
import { IconWrapper } from './_atoms/IconWrapper';

const Portal = ({ children }: { children: React.ReactNode }) => {
  return typeof document === 'object' ? createPortal(children, document.body) : null;
};

const scaleFromCenter = {
  hidden: {
    opacity: 0,
    transform: 'scale(0)',
  },
  visible: {
    opacity: 1,
    transform: 'scale(1)',
    transition: {
      delay: 0.1,
      duration: 0.2,
    },
  },
  exit: {
    opacity: 0,
    transform: 'scale(0)',
  },
};

const maskOpacity = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
    },
  },
  exit: {
    opacity: 0,
  },
};

export const ReadAloudModal = ({
  onClose,
  margin = 100,
  children,
}: {
  margin?: number;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  return (
    <Portal>
      <ReactHotkeys
        keyName={'Escape'}
        onKeyDown={(_, event) => {
          event.preventDefault();
          onClose();
        }}
      />
      <AnimatePresence>
        <Mask
          onClick={onClose}
          variants={maskOpacity}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <ResetHeightWidthContainer>
            <MaxSizeContainer $margin={margin}>
              <ResetHeightWidthContainer>
                <ContentContainer
                  onClick={e => {
                    // Need this or else the mask onClick will be triggered
                    e.stopPropagation();
                  }}
                  $padding={20}
                  variants={scaleFromCenter}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <CloseContainer onClick={onClose}>
                    <IconWrapper>
                      <CrossSvg />
                    </IconWrapper>
                  </CloseContainer>
                  {children}
                </ContentContainer>
              </ResetHeightWidthContainer>
            </MaxSizeContainer>
          </ResetHeightWidthContainer>
        </Mask>
      </AnimatePresence>
    </Portal>
  );
};

const ResetHeightWidthContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

const CloseContainer = styled.div`
  position: absolute;
  top: -20px;
  right: -75px;
  z-index: ${zIndexes('FULL_SIZE_MODAL')};
  height: 50px;
  width: 50px;
  display: grid;
  justify-content: center;
  align-items: center;
  background: rgba(17, 23, 29, 0.6);
  color: white;
  border-radius: 100px;
  box-shadow: 0px 0px 0px 1px rgba(64, 87, 109, 0.04),
    0px 6px 20px -4px rgba(64, 87, 109, 0.3);

  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    background: black;
  }
`;

const ContentContainer = styled(motion.div)<{ $padding: number }>`
  // Reset styles
  position: relative;
  display: block;

  width: 100%;
  height: 100%;
  /* overflow-y: auto; */

  // Styles
  border-radius: 10px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);

  ${({ $padding }) => css`
    /* Border radius and outline hack for safari <- works on all browsers */
    &::after {
      position: absolute;
      z-index: -1; // to allow click events in the children
      top: -${$padding}px;
      content: '';
      left: -${$padding}px;
      width: calc(100% + ${$padding * 2}px);
      height: calc(100% + ${$padding * 2}px);
      border: ${$padding}px solid ${theme.colors.white};
      border-radius: 10px;
      display: inherit;
    }
  `}
`;

const MaxSizeContainer = styled.div<{ $margin?: number }>`
  position: absolute;

  ${({ $margin }) =>
    $margin &&
    css`
      height: calc(100% - ${$margin * 2}px);
      width: 600px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    `}
`;

const Mask = styled(motion.div)`
  z-index: ${zIndexes('FULL_SIZE_MODAL')};
  position: fixed; // Prevent scrolling the modal mask
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;

  background: rgba(0, 0, 0, 0.6);
`;
